import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Copy, Check, Gift, TrendingUp, DollarSign } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import Sidebar from '../components/Sidebar';

const ReferralsPage = () => {
  const [copied, setCopied] = useState(false);
  const referralCode = 'DUTYAI-JD2025';
  const referralLink = `https://dutyai.com/join?ref=${referralCode}`;

  const stats = {
    totalReferrals: 12,
    activeReferrals: 8,
    totalEarned: 2400,
    pendingEarnings: 600
  };

  const referrals = [
    { id: 1, name: 'Sarah Johnson', status: 'active', earned: 200, joinedDate: '2025-01-15' },
    { id: 2, name: 'Mike Chen', status: 'active', earned: 200, joinedDate: '2025-01-10' },
    { id: 3, name: 'Emily Rodriguez', status: 'pending', earned: 0, joinedDate: '2025-01-25' },
    { id: 4, name: 'Arun Kumar', status: 'active', earned: 200, joinedDate: '2025-01-05' },
    { id: 5, name: 'Jessica Lee', status: 'active', earned: 200, joinedDate: '2024-12-20' },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast({
      title: 'Copied!',
      description: 'Referral link copied to clipboard'
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-6xl mx-auto px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Referrals</h1>
            <p className="text-gray-600">Invite friends and earn rewards together</p>
          </div>

          {/* Referral Link Card */}
          <Card className="p-8 mb-8 bg-[#f0f5ed] border-[#B8C4AD]">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-[#0A3900] rounded-full">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Earn $200 per referral</h2>
                <p className="text-gray-700">Share Duty AI with friends and get rewarded when they complete their first job</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-4 flex items-center gap-3">
              <Input value={referralLink} readOnly className="flex-1" />
              <Button onClick={handleCopy} className="gap-2 bg-[#0A3900] hover:bg-[#1A5C0A]">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>

            <div className="mt-4 text-sm text-gray-600">
              <p><strong>Your referral code:</strong> {referralCode}</p>
            </div>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Referrals</p>
                <Users className="w-5 h-5 text-[#0A3900]" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.totalReferrals}</p>
              <p className="text-sm text-gray-500 mt-2">All time</p>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Active</p>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.activeReferrals}</p>
              <p className="text-sm text-gray-500 mt-2">Earning rewards</p>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Earned</p>
                <DollarSign className="w-5 h-5 text-[#0A3900]" />
              </div>
              <p className="text-3xl font-bold text-gray-900">${stats.totalEarned}</p>
              <p className="text-sm text-green-600 mt-2">+$200 this month</p>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Pending</p>
                <DollarSign className="w-5 h-5 text-yellow-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">${stats.pendingEarnings}</p>
              <p className="text-sm text-gray-500 mt-2">Processing</p>
            </Card>
          </div>

          {/* How It Works */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">How It Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#E0E8D0] text-[#0A3900] rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Share Your Link</h3>
                <p className="text-sm text-gray-600">Send your unique referral link to friends interested in AI work</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#E0E8D0] text-[#0A3900] rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">They Sign Up</h3>
                <p className="text-sm text-gray-600">Your friends create an account and complete their profile</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#E0E8D0] text-[#0A3900] rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Earn Rewards</h3>
                <p className="text-sm text-gray-600">Get $200 when they complete their first paid project</p>
              </div>
            </div>
          </Card>

          {/* Referrals List */}
          <Card>
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Your Referrals</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Earned
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {referrals.map((referral) => (
                    <tr key={referral.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <img
                            src={`https://i.pravatar.cc/150?u=${referral.name}`}
                            alt={referral.name}
                            className="w-10 h-10 rounded-full"
                          />
                          <span className="font-medium text-gray-900">{referral.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            referral.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {referral.status === 'active' ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${referral.earned}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {referral.joinedDate}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReferralsPage;